const WHITE_LABEL_STORAGE_KEY = "whiteLabelConfig";
const THEME_STORAGE_KEY = "themeName";
const DEFAULT_APP_TITLE = "SkaleBot";
const DEFAULT_FAVICON_PATH = `${process.env.PUBLIC_URL}/images/skalebotnw.png`;

const hasBrowserWindow = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readJsonFromStorage = (key) => {
  if (!hasBrowserWindow()) return null;

  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    console.error(`Failed to read ${key} from storage:`, error);
    return null;
  }
};

const writeJsonToStorage = (key, value) => {
  if (!hasBrowserWindow()) return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to write ${key} to storage:`, error);
  }
};

const upsertLink = (rel, href) => {
  if (typeof document === "undefined") return;

  let link = document.querySelector(`link[rel="${rel}"]`);

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
};

export const getStoredWhiteLabelResponse = () =>
  readJsonFromStorage(WHITE_LABEL_STORAGE_KEY);

export const getStoredWhiteLabelConfig = () =>
  getStoredWhiteLabelResponse()?.data || null;

export const resolveBrandDisplayName = (
  config = getStoredWhiteLabelConfig(),
  fallback = DEFAULT_APP_TITLE
) => config?.title?.trim() || config?.name?.trim() || fallback;

export const replaceDefaultBrandName = (
  text,
  config = getStoredWhiteLabelConfig()
) => {
  if (typeof text !== "string" || !text) return text;

  const brandName = resolveBrandDisplayName(config);
  return text.replace(/SkaleBot|Skalebot|SKALEBOT/g, (match) => {
    if (match === "SKALEBOT") {
      return brandName.toUpperCase();
    }

    return brandName;
  });
};

export const resolveWhiteLabelThemeName = (config = {}) =>
  config?.themeName || config?.metaData?.theme || null;

export const getStoredThemeName = () => {
  if (!hasBrowserWindow()) return null;

  const storedThemeName = window.localStorage.getItem(THEME_STORAGE_KEY);
  return storedThemeName || resolveWhiteLabelThemeName(getStoredWhiteLabelConfig());
};

export const mergeWhiteLabelResponse = (
  nextResponse,
  currentResponse = getStoredWhiteLabelResponse()
) => {
  if (!nextResponse?.data) {
    return currentResponse || null;
  }

  if (!currentResponse?.data) {
    return nextResponse;
  }

  return {
    ...currentResponse,
    ...nextResponse,
    data: {
      ...currentResponse.data,
      ...nextResponse.data,
    },
  };
};

export const persistWhiteLabelResponse = (response) => {
  const mergedResponse = mergeWhiteLabelResponse(response);

  if (!mergedResponse?.data) {
    return null;
  }

  writeJsonToStorage(WHITE_LABEL_STORAGE_KEY, mergedResponse);

  const themeName = resolveWhiteLabelThemeName(mergedResponse.data);
  if (themeName && hasBrowserWindow()) {
    window.localStorage.setItem(THEME_STORAGE_KEY, themeName.toLowerCase());
  }

  return mergedResponse;
};

export const clearAppStoragePreserveBranding = () => {
  if (!hasBrowserWindow()) return;

  const whiteLabelResponse = getStoredWhiteLabelResponse();
  const themeName = window.localStorage.getItem(THEME_STORAGE_KEY);

  window.localStorage.clear();

  if (whiteLabelResponse) {
    writeJsonToStorage(WHITE_LABEL_STORAGE_KEY, whiteLabelResponse);
  }

  if (themeName) {
    window.localStorage.setItem(THEME_STORAGE_KEY, themeName);
  }
};

export const applyWhiteLabelBranding = (config = getStoredWhiteLabelConfig()) => {
  if (typeof document === "undefined") {
    return;
  }

  const title = resolveBrandDisplayName(config);
  const favicon = config?.favIcon?.trim() || DEFAULT_FAVICON_PATH;

  document.title = title;
  upsertLink("icon", favicon);
  upsertLink("shortcut icon", favicon);
  upsertLink("apple-touch-icon", favicon);
};
